import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ArrowRight, ArrowLeft, Check, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

const STEPS = [
  { key: "name", title: "What should I call you?", subtitle: "Pick a name that feels right." },
  { key: "age", title: "Tell me about yourself", subtitle: "This helps me communicate in a way that works for you." },
  { key: "heritage", title: "Your roots matter", subtitle: "I'll draw from your cultural traditions when sharing wisdom." },
  { key: "beliefs", title: "What guides you?", subtitle: "I respect all paths. This helps me speak your language of meaning." },
  { key: "environment", title: "Where's home?", subtitle: "Context helps me give better guidance." },
  { key: "safeguard", title: "One important thing", subtitle: "Before we begin, a promise between us." },
];

const AGE_OPTIONS = [
  { value: "child", label: "Under 13" },
  { value: "teen", label: "13-17" },
  { value: "adult", label: "18-64" },
  { value: "senior", label: "65+" },
];

const BELIEF_OPTIONS = [
  "Christianity", "Islam", "Hinduism", "Buddhism", "Judaism",
  "Sikhism", "Traditional/Indigenous", "Spiritual (non-religious)",
  "Agnostic", "Atheist/Secular Humanist", "Other", "Prefer not to say"
];

const SETTING_OPTIONS = [
  { value: "urban", label: "Urban / City" },
  { value: "suburban", label: "Suburban" },
  { value: "rural", label: "Rural / Countryside" },
];

const FAMILY_OPTIONS = [
  "Nuclear family", "Single-parent household", "Extended family",
  "Living alone", "Living with partner", "Other"
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { token, user, updateUser } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [persona, setPersona] = useState({
    preferred_name: "",
    age_group: "",
    gender_identity: "",
    heritage: { ethnicity: "", nationality: "", cultural_traditions: "" },
    belief_system: { religion: "", spiritual_practices: "" },
    environment: { setting: "", family_structure: "" },
  });

  const headers = { Authorization: `Bearer ${token}` };

  const savePersona = async () => {
    try {
      await axios.post(`${API}/onboarding/persona`, persona, { headers });
    } catch (err) {
      console.error("Save persona failed:", err);
    }
  };

  const confirmSafeguard = async () => {
    setLoading(true);
    try {
      await savePersona();
      await axios.post(`${API}/onboarding/safeguard`, { confirmed: true }, { headers });
      updateUser({ onboarding_completed: true, persona });
      toast.success("Welcome to Omni! Let's begin.");
      navigate("/chat");
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const next = () => {
    if (step < STEPS.length - 1) {
      if (step > 0) savePersona();
      setStep(step + 1);
    }
  };

  const back = () => { if (step > 0) setStep(step - 1); };

  const canProceed = () => {
    switch (step) {
      case 0: return persona.preferred_name.trim().length > 0;
      case 1: return persona.age_group.length > 0;
      default: return true;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
          <span className="font-heading text-xl">Omni</span>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-10 px-4">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${i <= step ? "bg-primary" : "bg-muted"}`} />
          ))}
        </div>

        <div className="card-omni p-8 md:p-10">
          <h2 className="font-heading text-2xl font-medium mb-2">{STEPS[step].title}</h2>
          <p className="text-sm text-muted-foreground mb-8">{STEPS[step].subtitle}</p>

          {/* Step: Name */}
          {step === 0 && (
            <input
              data-testid="onboard-name-input"
              type="text"
              value={persona.preferred_name}
              onChange={e => setPersona({ ...persona, preferred_name: e.target.value })}
              className="input-omni w-full border border-input text-lg"
              placeholder="Your preferred name"
              autoFocus
            />
          )}

          {/* Step: Age & Gender */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium mb-3 block">Age Group</label>
                <div className="grid grid-cols-2 gap-3">
                  {AGE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      data-testid={`age-${opt.value}`}
                      onClick={() => setPersona({ ...persona, age_group: opt.value })}
                      className={`p-4 rounded-2xl border-2 transition-all text-left ${
                        persona.age_group === opt.value
                          ? "border-primary bg-primary/5"
                          : "border-border/50 hover:border-primary/30"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Gender Identity <span className="text-muted-foreground font-normal">(optional)</span></label>
                <input
                  data-testid="onboard-gender-input"
                  type="text"
                  value={persona.gender_identity}
                  onChange={e => setPersona({ ...persona, gender_identity: e.target.value })}
                  className="input-omni w-full border border-input"
                  placeholder="However you identify"
                />
              </div>
            </div>
          )}

          {/* Step: Heritage */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <label className="text-sm font-medium mb-2 block">Ethnicity / Cultural Background</label>
                <input
                  data-testid="onboard-ethnicity-input"
                  value={persona.heritage.ethnicity}
                  onChange={e => setPersona({ ...persona, heritage: { ...persona.heritage, ethnicity: e.target.value } })}
                  className="input-omni w-full border border-input"
                  placeholder="e.g., Yoruba, South Asian, Irish-American"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Nationality</label>
                <input
                  data-testid="onboard-nationality-input"
                  value={persona.heritage.nationality}
                  onChange={e => setPersona({ ...persona, heritage: { ...persona.heritage, nationality: e.target.value } })}
                  className="input-omni w-full border border-input"
                  placeholder="e.g., Nigerian, Indian, American"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Cultural Traditions <span className="text-muted-foreground font-normal">(optional)</span></label>
                <input
                  data-testid="onboard-traditions-input"
                  value={persona.heritage.cultural_traditions}
                  onChange={e => setPersona({ ...persona, heritage: { ...persona.heritage, cultural_traditions: e.target.value } })}
                  className="input-omni w-full border border-input"
                  placeholder="e.g., Collectivist values, Western individualism"
                />
              </div>
            </div>
          )}

          {/* Step: Beliefs */}
          {step === 3 && (
            <div className="space-y-5">
              <div className="flex flex-wrap gap-2">
                {BELIEF_OPTIONS.map(b => (
                  <button
                    key={b}
                    data-testid={`belief-${b.replace(/\s+/g, '-').toLowerCase()}`}
                    onClick={() => setPersona({ ...persona, belief_system: { ...persona.belief_system, religion: b } })}
                    className={`px-4 py-2 rounded-full border-2 text-sm transition-all ${
                      persona.belief_system.religion === b
                        ? "border-primary bg-primary/5 text-primary font-semibold"
                        : "border-border/50 hover:border-primary/30"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Spiritual Practices <span className="text-muted-foreground font-normal">(optional)</span></label>
                <input
                  data-testid="onboard-spiritual-input"
                  value={persona.belief_system.spiritual_practices}
                  onChange={e => setPersona({ ...persona, belief_system: { ...persona.belief_system, spiritual_practices: e.target.value } })}
                  className="input-omni w-full border border-input"
                  placeholder="e.g., Daily prayer, meditation, none"
                />
              </div>
            </div>
          )}

          {/* Step: Environment */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium mb-3 block">Living Environment</label>
                <div className="grid grid-cols-3 gap-3">
                  {SETTING_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      data-testid={`env-${opt.value}`}
                      onClick={() => setPersona({ ...persona, environment: { ...persona.environment, setting: opt.value } })}
                      className={`p-4 rounded-2xl border-2 text-center transition-all ${
                        persona.environment.setting === opt.value
                          ? "border-primary bg-primary/5"
                          : "border-border/50 hover:border-primary/30"
                      }`}
                    >
                      <span className="text-sm">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-3 block">Family Structure</label>
                <div className="flex flex-wrap gap-2">
                  {FAMILY_OPTIONS.map(f => (
                    <button
                      key={f}
                      data-testid={`family-${f.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => setPersona({ ...persona, environment: { ...persona.environment, family_structure: f } })}
                      className={`px-4 py-2 rounded-full border-2 text-sm transition-all ${
                        persona.environment.family_structure === f
                          ? "border-primary bg-primary/5 text-primary font-semibold"
                          : "border-border/50 hover:border-primary/30"
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step: Safeguard */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="p-6 bg-primary/5 rounded-2xl border border-primary/10">
                <div className="flex items-start gap-4">
                  <Shield className="w-8 h-8 text-primary flex-shrink-0 mt-1" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-heading text-lg font-medium mb-3">The Safeguard Promise</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                      I, <strong>{persona.preferred_name || "User"}</strong>, understand that:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-3 leading-relaxed">
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Omni is an AI companion, <strong>not</strong> a licensed medical, legal, or financial professional.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>In a crisis or emergency, I will contact professional emergency services or a crisis helpline.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>All support is provided via AI. Omni does not offer human, phone, or in-person support.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>AI guidance does not replace professional advice and should not be solely relied upon for critical decisions.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10 gap-4">
            {step > 0 ? (
              <Button variant="ghost" onClick={back} className="rounded-full gap-2 text-muted-foreground">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
            ) : <div />}

            {step < 5 ? (
              <Button
                data-testid="onboard-next-btn"
                onClick={next}
                disabled={!canProceed()}
                className="btn-omni bg-primary text-primary-foreground gap-2"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                data-testid="onboard-confirm-btn"
                onClick={confirmSafeguard}
                disabled={loading}
                className="btn-omni bg-primary text-primary-foreground gap-2"
              >
                {loading ? "..." : "I understand, let's begin"}
              </Button>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-6">
          All steps optional except the safeguard confirmation. You can update these anytime in Settings.
        </p>
      </div>
    </div>
  );
}
